import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  contentChild,
  contentChildren,
  effect,
  forwardRef,
  inject,
  input,
  model,
} from '@angular/core';
import { MENU, MenuApi, MenuItemComponent } from './menu-item.component';

/** Lado do gatilho em que o painel abre. */
export type MenuPosicao = 'baixo-inicio' | 'baixo-fim' | 'cima-inicio' | 'direita-fim';

const CLASSES_PAINEL =
  'absolute z-50 flex flex-col gap-0.5 rounded-xl bg-rfy-paper p-[0.375rem] ' +
  'border-solid border-[1px] border-rfy-border-strong shadow-xl shadow-black/10 dark:shadow-black/50';

/** Largura do painel: `padrao` acompanha o conteúdo; `larga` é fixa (ex.: menu da navegação lateral). */
export type MenuLargura = 'padrao' | 'larga';

const CLASSES_LARGURA: Record<MenuLargura, string> = {
  padrao: 'min-w-[12rem]',
  larga: 'w-[15.5rem]',
};

const CLASSES_POSICAO: Record<MenuPosicao, string> = {
  'baixo-inicio': 'top-full mt-[0.375rem] left-[0]',
  'baixo-fim': 'top-full mt-[0.375rem] right-[0]',
  'cima-inicio': 'bottom-full mb-[0.375rem] left-[0]',
  'direita-fim': 'left-full ml-[0.375rem] bottom-[0]',
};

let proximoId = 0;

/** Marca o elemento que abre/fecha o menu (filho de conteúdo do `app-menu`). */
@Directive({
  selector: '[appMenuGatilho]',
  standalone: true,
  host: {
    'aria-haspopup': 'menu',
    '[attr.aria-expanded]': 'menu.aberto() ? "true" : "false"',
    '[attr.aria-controls]': 'menu.aberto() ? menu.idPainel : null',
    '(click)': 'menu.alternar()',
  },
})
export class MenuGatilhoDirective {
  protected readonly menu = inject(MenuComponent);
  readonly element: HTMLElement = inject(ElementRef).nativeElement;
}

/** Título de seção dentro do painel. */
@Directive({
  selector: '[appMenuRotulo]',
  standalone: true,
  host: { class: 'px-[0.625rem] pt-[0.375rem] pb-[0.25rem] text-[0.6875rem] font-bold uppercase tracking-wider text-rfy-muted' },
})
export class MenuRotuloDirective {}

/** Separador horizontal dentro do painel. */
@Component({
  selector: 'app-menu-divisor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
  host: { role: 'separator', class: 'my-[0.25rem] block h-px bg-rfy-border' },
})
export class MenuDivisorComponent {}

/**
 * Menu suspenso acessível (Esc, ↑/↓, Home/End, clique fora fecha):
 *
 * ```html
 * <app-menu rotulo="Ações da linha">
 *   <button app-button appMenuGatilho variante="ghost" icone="mais-opcoes" quadrado aria-label="Ações"></button>
 *   <button app-menu-item icone="editar" (click)="editar(item)">Editar</button>
 *   <app-menu-divisor />
 *   <button app-menu-item icone="excluir" (click)="excluir(item)">Excluir</button>
 * </app-menu>
 * ```
 */
@Component({
  selector: 'app-menu',
  standalone: true,
  templateUrl: './menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MENU, useExisting: forwardRef(() => MenuComponent) }],
  host: {
    class: 'relative inline-flex',
    '(document:click)': 'aoClicarDocumento($event)',
  },
})
export class MenuComponent implements MenuApi {
  private readonly element: HTMLElement = inject(ElementRef).nativeElement;
  private readonly injector = inject(Injector);

  readonly aberto = model(false);
  readonly posicao = input<MenuPosicao>('baixo-fim');
  readonly largura = input<MenuLargura>('padrao');
  /** Nome acessível do painel. */
  readonly rotulo = input.required<string>();

  readonly idPainel = `app-menu-${proximoId++}-painel`;

  protected readonly itens = contentChildren(MenuItemComponent, { descendants: true });
  private readonly gatilho = contentChild(MenuGatilhoDirective);
  protected readonly classesPainel = computed(
    () => `${CLASSES_PAINEL} ${CLASSES_LARGURA[this.largura()]} ${CLASSES_POSICAO[this.posicao()]}`,
  );

  constructor() {
    effect(() => {
      // afterNextRender não roda no servidor: sem acesso ao DOM no SSR.
      if (this.aberto()) afterNextRender(() => this.focarItemInicial(), { injector: this.injector });
    });
  }

  alternar(): void {
    this.aberto.update((aberto) => !aberto);
  }

  fechar(devolverFoco = false): void {
    if (!this.aberto()) return;
    this.aberto.set(false);
    if (devolverFoco) this.gatilho()?.element.focus();
  }

  protected aoTeclar(evento: KeyboardEvent): void {
    if (evento.key === 'Escape') {
      evento.preventDefault();
      this.fechar(true);
      return;
    }
    // Tab segue o fluxo normal de foco, só fecha o painel.
    if (evento.key === 'Tab') {
      this.fechar();
      return;
    }

    const itens = this.itens();
    if (!itens.length) return;
    const atual = itens.findIndex((item) => item.element === evento.target);
    let proximo: number;
    switch (evento.key) {
      case 'ArrowDown':
        proximo = atual < 0 ? 0 : (atual + 1) % itens.length;
        break;
      case 'ArrowUp':
        proximo = atual < 0 ? itens.length - 1 : (atual - 1 + itens.length) % itens.length;
        break;
      case 'Home':
        proximo = 0;
        break;
      case 'End':
        proximo = itens.length - 1;
        break;
      default:
        return;
    }
    evento.preventDefault();
    itens[proximo].focar();
  }

  protected aoClicarDocumento(evento: Event): void {
    if (!this.aberto()) return;
    const alvo = evento.target;
    if (alvo instanceof Node && this.element.contains(alvo)) return;
    this.aberto.set(false);
  }

  private focarItemInicial(): void {
    const itens = this.itens();
    (itens.find((item) => item.marcado() === true) ?? itens[0])?.focar();
  }
}
